import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useEffect, type Ref } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { useViewingAs } from '@/auth/useViewingAs';

/**
 * Says, on every page, that staff are looking at an organization read-only,
 * and offers the way out.
 *
 * A status region, so its arrival is announced when the view begins. It sits
 * inside the fixed top bar, which is the one place that is both after the skip
 * link and before the navigation for somebody moving by keyboard. It is the
 * top row, above the bar itself, so everything below it reads as the
 * organization's own portal.
 */
export function ViewingBanner({ ref }: { ref?: Ref<HTMLDivElement> }) {
  const { viewing, exit, arriving, arrived } = useViewingAs();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /*
   * A view ends once a staff page is the one on screen, however it got there:
   * Exit, the browser's Back button, a typed address or a reload. Back changes
   * only the address, and a view left on over a staff page kept the
   * organization's banner and sections there and refused every staff save as
   * read-only.
   *
   * Ending it on arrival rather than on the click: ended any earlier, the
   * organization page still showing has no view to allow it, and the route
   * guard sends staff to the no-access page - the router applies a navigation
   * a moment after it is asked for. The one staff page that does not end a
   * view is the one View portal was pressed on, still on screen while the
   * router moves to the dashboard.
   */
  const onStaffPage = pathname.startsWith('/staff/');
  useEffect(() => {
    if (!viewing) return;
    if (!onStaffPage) {
      if (arriving) arrived();
      return;
    }
    if (!arriving) exit();
  }, [viewing, onStaffPage, arriving, arrived, exit]);

  if (!viewing) return null;
  const viewedId = viewing.id;

  function goBack() {
    void navigate(ROUTES.staff.organization(viewedId));
  }

  return (
    <Box ref={ref}>
      <Alert
        severity="info"
        role="status"
        square
        data-testid="viewing-banner"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={goBack}
            data-testid="viewing-banner-exit-button"
          >
            Exit
          </Button>
        }
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          // On a narrow screen the words wrap and the button drops beneath
          // them, rather than pushing the page sideways.
          flexWrap: 'wrap',
          '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
          '& .MuiAlert-action': { ml: { xs: 0, sm: 'auto' }, pl: { xs: 0, sm: 2 } },
        }}
      >
        Viewing <strong data-testid="viewing-banner-name-value">{viewing.name}</strong> as
        Activist staff - read-only
      </Alert>
    </Box>
  );
}
